import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, tap, takeUntil } from 'rxjs/operators';
import { DeleteConfirmationComponent } from './delete-confirmation/delete-confirmation.component';
import { ModalBaseComponent } from './modal-base.component';
import { ChangesConfirmationComponent } from 'app/core/modal/changes-confirmation';
import { Actions, ofType } from '@ngrx/effects';
import { DestroyChecker } from 'app/helpers';
import { CoreActionTypes } from 'app/admin/store/core/core-action-types.enum';

interface ModalClass<T extends ModalBaseComponent> {
  new (...args): T;
  prototype: T;
}

interface OpenResult<T extends ModalBaseComponent> {
  component: T;
  result$: T['result$'];
  modalRef: BsModalRef;
}

@Injectable()
export class ModalService extends DestroyChecker {
  public modalRefs: BsModalRef[] = [];

  constructor(
    private bsModalService: BsModalService,
    private actions$: Actions
  ) {
    super();
    this.bsModalService.onHide.subscribe((modal) =>
      ModalService.triggerOnCloseDOMEvents(modal)
    );
    this.actions$
      .pipe(
        ofType(CoreActionTypes.LOG_OUT),
        tap(() => {
          this.closeAll();
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }
  private errors$ = new Subject();
  private loading$ = new BehaviorSubject(false);

  openDeleteConfirmation(
    title: string,
    description: string = '',
    actionLabel: string = 'YES, DELETE'
  ): Observable<boolean> {
    const { result$ } = this.openModal<DeleteConfirmationComponent>(
      DeleteConfirmationComponent,
      {
        title,
        description: description
          .replace(/\r?\n|\r/g, '')
          .split(' ')
          .filter((val) => !!val)
          .join(' '),
        actionLabel
      }
    );

    return result$.pipe(
      map((result) => {
        if (result) {
          this.closeModal();
        }
        return result;
      })
    );
  }

  openChangesConfirmation(
    title: string,
    description: string = '',
    confirmText: string = 'YES, CONFIRM!'
  ): Observable<boolean> {
    const { result$ } = this.openModal<ChangesConfirmationComponent>(
      ChangesConfirmationComponent,
      {
        title,
        description: description
          .replace(/\r?\n|\r/g, '')
          .split(' ')
          .filter((val) => !!val)
          .join(' '),
        confirmText
      }
    );

    return result$.pipe(
      map((result) => {
        if (result) {
          this.closeModal();
        }
        return result;
      })
    );
  }

  openModal<T extends ModalBaseComponent>(
    component: ModalClass<T>,
    data: { [key in keyof T]?: any } = null,
    size: string = null
  ) {
    this.resetModalState();
    this.modalRefs.unshift(
      this.bsModalService.show(component, {
        initialState: {
          ...data,
          loading$: this.loading$.asObservable(),
          errors$: this.errors$.asObservable()
        },
        ignoreBackdropClick: true,
        keyboard: false,
        class: `modal-dialog-${size}`
      })
    );
    return this.getResult<T>();
  }

  closeModal() {
    if (this.modalRefs.length === 0) {
      return;
    }
    const modalRef = this.modalRefs.shift();
    modalRef.hide();
  }

  closeAll() {
    this.modalRefs.forEach((modalRef) => modalRef.hide());
    this.modalRefs = [];
  }

  toggleModalLoading(value = !this.loading$.value) {
    this.loading$.next(value);
  }

  setModalErrors(errors) {
    this.toggleModalLoading(false);
    this.errors$.next(errors);
  }

  onModalError() {
    // eslint-disable-next-line
    return catchError((errors: any): Observable<boolean> => {
      this.setModalErrors(errors);
      return of(false);
    });
  }

  private getResult<T extends ModalBaseComponent>(): OpenResult<T> {
    if (this.modalRefs.length === 0) {
      return;
    }
    const modalRef = this.modalRefs[0];
    const modalComponent: T = modalRef.content;

    return {
      component: modalComponent,
      modalRef,
      result$: modalComponent.result$.pipe(
        tap(
          (result) => (!result || result === 'hasChanges') && this.closeModal()
        )
      )
    };
  }

  private resetModalState() {
    this.errors$.next();
    this.loading$.next(false);
  }

  private static triggerOnCloseDOMEvents(modal) {
    const elements = document?.getElementsByTagName('body') ?? [];
    for (const key in Object.keys(elements)) {
      if (elements[key]) {
        elements[key].dispatchEvent(new Event('hidden.bs.modal'));
        elements[key].dispatchEvent(
          new Event(`hidden.${modal.content.name}.modal`)
        );
      }
    }
  }
}
